import {
  ISurveyContract,
  ISurveyQuestion,
  ISurveyVisibilityRule,
} from "../zeos-api/contracts/Survey";
import { QuestionTypeEnum } from "../zeos-api/enums/Survey";

export const isQuestionType = (
  questionTypeId: QuestionTypeEnum | number | string | undefined,
  expected: QuestionTypeEnum
): boolean => Number(questionTypeId) === expected;

export const flattenSurveyQuestions = (
  template: ISurveyContract
): ISurveyQuestion[] => {
  return [...template.questionnaires]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .flatMap((qn) =>
      [...qn.questions].sort((a, b) => a.sortOrder - b.sortOrder)
    );
};

export const getGateQuestion = (
  template: ISurveyContract
): ISurveyQuestion | undefined => {
  return flattenSurveyQuestions(template)[0];
};

const optionIsSelected = (
  answers: Record<number, any>,
  dependsOnQuestionId: number,
  optionId: number
): boolean => {
  const value = answers[dependsOnQuestionId];
  if (Array.isArray(value)) {
    return value.some((v) => Number(v) === Number(optionId));
  }
  return value != null && value !== "" && Number(value) === Number(optionId);
};

/**
 * Shared visibility evaluation for question / questionnaire rules from API.
 *
 * - No rules => visible
 * - Any matching IsHideRule => hidden
 * - Else if show-rules exist => visible only when every DependsOnQuestionId
 *   group has at least one matching OptionId (AND across parents, OR within)
 * - Else (only hide rules, none matched) => visible
 */
export const evaluateVisibilityRules = (
  rules: ISurveyVisibilityRule[] | null | undefined,
  answers: Record<number, any>
): boolean => {
  const active = (rules ?? []).filter((r) => r != null);
  if (active.length === 0) {
    return true;
  }

  const hideRules = active.filter((r) => r.isHideRule);
  const showRules = active.filter((r) => !r.isHideRule);

  for (const rule of hideRules) {
    if (
      optionIsSelected(answers, rule.dependsOnQuestionId, rule.optionId)
    ) {
      return false;
    }
  }

  if (showRules.length === 0) {
    return true;
  }

  const byParent = new Map<number, ISurveyVisibilityRule[]>();
  showRules.forEach((rule) => {
    const list = byParent.get(rule.dependsOnQuestionId) ?? [];
    list.push(rule);
    byParent.set(rule.dependsOnQuestionId, list);
  });

  for (const [, group] of byParent) {
    const matched = group.some((rule) =>
      optionIsSelected(answers, rule.dependsOnQuestionId, rule.optionId)
    );
    if (!matched) {
      return false;
    }
  }

  return true;
};

export const isQuestionVisible = (
  question: ISurveyQuestion,
  answers: Record<number, any>
): boolean => evaluateVisibilityRules(question.visibilityRules, answers);

export const isQuestionnaireVisible = (
  rules: ISurveyVisibilityRule[] | null | undefined,
  answers: Record<number, any>
): boolean => evaluateVisibilityRules(rules, answers);

/** Drop answers for questions that are currently hidden. */
export const pruneHiddenAnswers = (
  template: ISurveyContract,
  answers: Record<number, any>
): Record<number, any> => {
  const next = { ...answers };
  let changed = false;

  template.questionnaires.forEach((qn) => {
    const qnVisible = isQuestionnaireVisible(qn.visibilityRules, answers);
    qn.questions.forEach((question) => {
      const visible =
        qnVisible && isQuestionVisible(question, answers);
      if (!visible && question.questionId in next) {
        delete next[question.questionId];
        changed = true;
      }
    });
  });

  return changed ? next : answers;
};

/** True when the first/gate-style interest answer is the "show more" path. */
export const isGateYesSelected = (
  template: ISurveyContract,
  answers: Record<number, any>
): boolean => {
  const gate = getGateQuestion(template);
  if (!gate) {
    return false;
  }
  // Prefer rules: if any follow-up is visible, gate unlocked the form.
  const followUps = flattenSurveyQuestions(template).filter(
    (q) => q.questionId !== gate.questionId
  );
  if (followUps.some((q) => (q.visibilityRules?.length ?? 0) > 0)) {
    return followUps.some((q) => isQuestionVisible(q, answers));
  }
  // Fallback for templates without rules (legacy).
  const yesOpt = gate.options?.find((o) =>
    (o.displayName || "").toLowerCase().includes("yes")
  );
  if (!yesOpt) {
    return false;
  }
  return optionIsSelected(answers, gate.questionId, yesOpt.responseOptionId);
};
